import { useState } from 'react';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Save, 
  RotateCcw,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Youtube,
  Music2,
  MessageCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { 
  contactSettings, 
  socialMediaSettings
} from '../config/brand-config';
// TODO: Implement update functions via API
const updateContactSettings = (settings: any) => ({ ...contactSettings, ...settings });
const updateSocialMediaSettings = (settings: any) => ({ ...socialMediaSettings, ...settings });
import { ContactSettings as ContactSettingsType, SocialMediaSettings as SocialMediaSettingsType } from '../types/brand-settings';

export function ContactSocialSettings() {
  const [contact, setContact] = useState<ContactSettingsType>(contactSettings);
  const [social, setSocial] = useState<SocialMediaSettingsType>(socialMediaSettings);
  const [hasChanges, setHasChanges] = useState(false);

  const handleContactChange = (field: keyof ContactSettingsType, value: string) => {
    setContact({ ...contact, [field]: value });
    setHasChanges(true);
  };

  const handleSocialChange = (field: keyof SocialMediaSettingsType, value: string) => {
    setSocial({ ...social, [field]: value });
    setHasChanges(true);
  };

  const handleSave = () => {
    updateContactSettings(contact);
    updateSocialMediaSettings(social);
    toast.success('Contact and social media settings saved successfully!', {
      description: 'Information updated across the platform'
    });
    setHasChanges(false);
  };

  const handleReset = () => {
    setContact(contactSettings);
    setSocial(socialMediaSettings);
    setHasChanges(false);
    toast.info('Changes reset to last saved settings');
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-2">Contact & Social Media Settings</h2>
        <p className="text-muted-foreground">
          Manage your company contact information and social media links
        </p>
      </div>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5" />
            Contact Information
          </CardTitle>
          <CardDescription>
            Update your company contact details displayed on the website
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={contact.phone}
                onChange={(e) => handleContactChange('phone', e.target.value)}
                placeholder="+27 (011) 123-4567"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={contact.email}
                onChange={(e) => handleContactChange('email', e.target.value)}
                placeholder="info@somethingproperty.co.za"
              />
            </div>
          </div>

          <Separator />

          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="address">Street Address</Label>
              <Input
                id="address"
                value={contact.address}
                onChange={(e) => handleContactChange('address', e.target.value)}
                placeholder="123 Main Street"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={contact.city}
                  onChange={(e) => handleContactChange('city', e.target.value)}
                  placeholder="Johannesburg"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={contact.country}
                  onChange={(e) => handleContactChange('country', e.target.value)}
                  placeholder="South Africa"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="postalCode">Postal Code</Label>
                <Input
                  id="postalCode"
                  value={contact.postalCode}
                  onChange={(e) => handleContactChange('postalCode', e.target.value)}
                  placeholder="2000"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Social Media Links */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            Social Media Links
          </CardTitle>
          <CardDescription>
            Add or update your social media profile links
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="facebook" className="flex items-center gap-2">
                <Facebook className="w-4 h-4 text-blue-600" />
                Facebook
              </Label>
              <Input
                id="facebook"
                type="url"
                value={social.facebook}
                onChange={(e) => handleSocialChange('facebook', e.target.value)}
                placeholder="https://facebook.com/yourpage"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="twitter" className="flex items-center gap-2">
                <Twitter className="w-4 h-4 text-blue-400" />
                Twitter / X
              </Label>
              <Input
                id="twitter"
                type="url"
                value={social.twitter}
                onChange={(e) => handleSocialChange('twitter', e.target.value)}
                placeholder="https://twitter.com/yourhandle"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="instagram" className="flex items-center gap-2">
                <Instagram className="w-4 h-4 text-pink-600" />
                Instagram
              </Label>
              <Input
                id="instagram"
                type="url"
                value={social.instagram}
                onChange={(e) => handleSocialChange('instagram', e.target.value)}
                placeholder="https://instagram.com/yourprofile"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="linkedin" className="flex items-center gap-2">
                <Linkedin className="w-4 h-4 text-blue-700" />
                LinkedIn
              </Label>
              <Input
                id="linkedin"
                type="url"
                value={social.linkedin}
                onChange={(e) => handleSocialChange('linkedin', e.target.value)}
                placeholder="https://linkedin.com/company/yourcompany"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="youtube" className="flex items-center gap-2">
                <Youtube className="w-4 h-4 text-red-600" />
                YouTube
              </Label>
              <Input
                id="youtube"
                type="url"
                value={social.youtube}
                onChange={(e) => handleSocialChange('youtube', e.target.value)}
                placeholder="https://youtube.com/yourchannel"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tiktok" className="flex items-center gap-2">
                <Music2 className="w-4 h-4 text-pink-500" />
                TikTok
              </Label>
              <Input
                id="tiktok"
                type="url"
                value={social.tiktok}
                onChange={(e) => handleSocialChange('tiktok', e.target.value)}
                placeholder="https://tiktok.com/@yourhandle"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="whatsapp" className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-green-600" />
                WhatsApp Number
              </Label>
              <Input
                id="whatsapp"
                type="tel"
                value={social.whatsapp}
                onChange={(e) => handleSocialChange('whatsapp', e.target.value)}
                placeholder="+27821234567"
              />
              <p className="text-xs text-muted-foreground">
                Enter with country code (e.g., +27821234567)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={handleReset}
          disabled={!hasChanges}
          data-testid="contact-social-settings-reset-button"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Reset Changes
        </Button>
        <Button
          onClick={handleSave}
          disabled={!hasChanges}
          data-testid="contact-social-settings-save-button"
        >
          <Save className="w-4 h-4 mr-2" />
          Save Settings
        </Button>
      </div>
    </div>
  );
}
